import { useMemo } from 'react'

import { MainContent, ModalContext, TopBar } from '@domain-group/suit-theme'

import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import useMainNavigation from '@hooks/useMainNavigation'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useReporting from '@hooks/useReporting'

import CreateOrEditTaskListModal from '@modals/CreateOrEditTaskListModal'
import CreateTemplateGroupModal from '@modals/CreateTemplateGroupModal/CreateTemplateGroupModal'
import GenerateReportModal from '@modals/GenerateReportModal'

import DashboardSearch from '@components/DashboardSearch'
import LoggedInUser from '@components/LoggedInUser'
import MainNavigation from '@sections/Admin/components/MainNavigation'

import defaultRequestOptions from '../../defaultRequestOptions'

import PageContent from './components/PageContent'

export type PageContextPayload = {
  callbacks: {
    resetFilters: () => void,
    showCreateOrEditTaskListModal: (payload: object) => void,
    showCreateTemplateGroupModal: () => void,
    showGenerateReportModal: () => void,
    updateFilters: (filters: object) => void,
  },
  defaultFilters: object,
}

type ComponentString = 'CreateOrEditTaskListModal' | 'CreateTemplateGroupModal' | 'GenerateReportModal'
const callbacks = (component: ComponentString, setState: any) => {
  const componentCallbacks = {
    CreateOrEditTaskListModal: {
      closeModal: () => setState({ showCreateOrEditTaskListModal: false }),
      createTaskList: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      createUsingUsageReport: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      toggleTaskListCompletion: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateTemplateGroupModal: {
      closeModal: () => setState({ showCreateTemplateGroupModal: false }),
      createTemplateGroup: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    GenerateReportModal: {
      closeModal: () => setState({ showGenerateReportModal: false }),
      generateTeamPerformanceReport: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateReportFormState: (formState: any) => setState({ reportFormState: formState }),
    },
  }

  return componentCallbacks[component]
}

const defaultState = {
  reportFormState: {
    end_date: '',
    reportKey: '' as 'template_rejections' | 'first_time_right_leaderboard' | 'active_projects' | '',
    start_date: '',
  },
  showCreateOrEditTaskListModal: false,
  showCreateTemplateGroupModal: false,
  showGenerateReportModal: false,
}

const Overview = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    reportFormState,
    showCreateOrEditTaskListModal,
    showCreateTemplateGroupModal,
    showGenerateReportModal,
  } = state

  const reportingPayload = useReporting({
    allowedReports: reportFormState.reportKey ? [reportFormState.reportKey as any] : [],
    defaultValues: {
      chart_data_key: reportFormState.reportKey || 'first_time_right_leaderboard',
      end_date: reportFormState.end_date || undefined,
      start_date: reportFormState.start_date || undefined,
    },
    formDeps: [reportFormState.reportKey, reportFormState.end_date, reportFormState.start_date],
  })

  const {
    callbacks: { generateReport },
    loading,
    saveEnabled,
  } = reportingPayload

  const { callbacks: { toggleNavigation } } = useMainNavigation()

  const {
    callbacks: filterCallbacks,
    defaultFilters,
    pageFilters,
  } = useFilters()

  const modalContext = useModals()
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showCreateOrEditTaskListModal: (payload: object) => {
        setModalData('CreateOrEditTaskListModal', payload)
        setState({ showCreateOrEditTaskListModal: true })
      },
      showCreateTemplateGroupModal: () => setState({ showCreateTemplateGroupModal: true }),
      showGenerateReportModal: () => setState({ showGenerateReportModal: true }),
    },
    defaultFilters,
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <TopBar
          callbacks={{ toggleNavigation }}
          loggedInUserComponent={<LoggedInUser />}
          searchComponent={(
            <DashboardSearch
              searchEntities={['LayoutTemplate', 'TaskList', 'TemplatePage', 'TemplatePagePartial']}
            />
          )}
        />

        {/*
         // @ts-expect-error TS2739 */}
        <MainNavigation />

        <MainContent offset={{ top: TopBar.topBarHeight }}>
          <PageContent pageFilters={pageFilters} />
        </MainContent>

        <CreateOrEditTaskListModal
          callbacks={callbacks('CreateOrEditTaskListModal', setState) as any}
          requestOptions={{
            ...defaultRequestOptions.taskList,
            include_task_list_task_counts: true,
          }}
          showModal={showCreateOrEditTaskListModal}
        />

        {/*
         // @ts-expect-error TS2741 */}
        <CreateTemplateGroupModal
          callbacks={callbacks('CreateTemplateGroupModal', setState) as any}
          requestOptions={{
            ...defaultRequestOptions.taskList,
            ...defaultRequestOptions.templateGroup,
            include_task_list_task_counts: true,
          }}
          showModal={showCreateTemplateGroupModal}
          titleSecondLine="Project"
        />

        <GenerateReportModal
          callbacks={callbacks('GenerateReportModal', setState) as any}
          formState={reportFormState}
          generateReport={generateReport}
          loading={loading}
          saveEnabled={saveEnabled}
          showModal={showGenerateReportModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Overview
